const CHUNK_RELOAD_STATE_KEY = '_wisp_chunk_reload_state_v1';
const CHUNK_RELOAD_COOLDOWN = 2 * 60 * 1000;

interface ChunkReloadState {
  href: string;
  ts: number;
}

const readReloadState = (): ChunkReloadState | null => {
  try {
    const raw = sessionStorage.getItem(CHUNK_RELOAD_STATE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const state = parsed as { href?: unknown; ts?: unknown };
    if (typeof state.href !== 'string' || typeof state.ts !== 'number') {
      return null;
    }

    return {
      href: state.href,
      ts: state.ts,
    };
  } catch {
    sessionStorage.removeItem(CHUNK_RELOAD_STATE_KEY);
    return null;
  }
};

const writeReloadState = (): void => {
  try {
    sessionStorage.setItem(
      CHUNK_RELOAD_STATE_KEY,
      JSON.stringify({
        href: window.location.href,
        ts: Date.now(),
      }),
    );
  } catch {
    // Safari 隐私模式等场景可能禁用 sessionStorage；刷新仍应继续执行。
  }
};

const toErrorMessage = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object') {
    const message = (value as { message?: unknown }).message;
    const name = (value as { name?: unknown }).name;
    return `${typeof name === 'string' ? name : ''} ${typeof message === 'string' ? message : ''}`;
  }

  return '';
};

const isChunkLoadMessage = (value: unknown): boolean => {
  const message = toErrorMessage(value);
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|CSS_CHUNK_LOAD_FAILED|dynamically imported module/i.test(message);
};

const isScriptOrStyleChunkTarget = (target: EventTarget | null): boolean => {
  if (target instanceof HTMLScriptElement) {
    const src = target.src || '';
    return /\.(js)(\?|$)/i.test(src) && (/\/static\//.test(src) || /chunk/i.test(src));
  }

  if (target instanceof HTMLLinkElement) {
    const href = target.href || '';
    return /\.(css)(\?|$)/i.test(href) && (/\/static\//.test(href) || /chunk/i.test(href));
  }

  return false;
};

const reloadPageOnce = (): void => {
  const state = readReloadState();
  const now = Date.now();
  if (state && state.href === window.location.href && now - state.ts < CHUNK_RELOAD_COOLDOWN) {
    return;
  }

  // 发版后旧 HTML 可能引用已清理的 chunk；刷新一次拿最新 HTML，比停在白屏更友好。
  writeReloadState();
  window.location.reload();
};

export const setupChunkLoadRecovery = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener(
    'error',
    event => {
      if (isScriptOrStyleChunkTarget(event.target) || isChunkLoadMessage(event.error) || isChunkLoadMessage(event.message)) {
        reloadPageOnce();
      }
    },
    true,
  );

  window.addEventListener(
    'unhandledrejection',
    event => {
      if (isChunkLoadMessage(event.reason)) {
        reloadPageOnce();
      }
    },
    true,
  );
};
